/**
 * Tenant-admin review page (Spec 005, repainted in Spec 009).
 *
 * Loaded for tenant_admin and platform_admin. Phase 9 layout: chat
 * is the primary surface; the workspace panel on the right carries
 * the customer-shared cards (photos, measurements) and an admin-only
 * block (notes, line-item override editor, review actions).
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { AdminRendersGallery } from "@/components/admin/AdminRendersGallery";
import { AdminReviewActions } from "@/components/admin/AdminReviewActions";
import { AdminSnapshotPanel } from "@/components/admin/AdminSnapshotPanel";
import { ApprovedSketchPanel } from "@/components/admin/ApprovedSketchPanel";
import { ProjectDimensionsPanel } from "@/components/admin/ProjectDimensionsPanel";
import { NotesPanel } from "@/components/admin/NotesPanel";
import { ChatPanel } from "@/components/chat/ChatPanel";
import { Lightbox } from "@/components/ui/Lightbox";
import { EstimateHeader } from "@/components/estimate/EstimateHeader";
import { LifecyclePanel } from "@/components/estimate/LifecyclePanel";
import { FormError } from "@/components/FormError";
import { DimensionsPanel } from "@/components/photos/DimensionsPanel";
import { PhotoGallery } from "@/components/photos/PhotoGallery";
import { MobileWorkspaceDrawer } from "@/components/workspace/MobileWorkspaceDrawer";
import { ProjectDetailsCard } from "@/components/workspace/ProjectDetailsCard";
import { WorkspacePanel } from "@/components/workspace/WorkspacePanel";
import { getEstimate, getEstimateDesign, listEstimateMessages } from "@starz/shared/api/sdk.gen";
import type { AdminEstimate, ChatMessage, Design, Estimate } from "@starz/shared/api/types.gen";
import { usePhotos } from "@starz/shared/photos/usePhotos";

export function AdminEstimateReview() {
  const { estimateId } = useParams<{ estimateId: string }>();
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  // Customer contact phone, captured once at load. Kept separate from
  // `estimate` because the approve/reject responses are plain Estimates
  // (no customer_* fields), so merging them back would drop the phone.
  const [customerPhone, setCustomerPhone] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [design, setDesign] = useState<Design | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(
    null,
  );
  const photosState = usePhotos(estimateId);

  useEffect(() => {
    if (!estimateId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [e, m, d] = await Promise.all([
        getEstimate({ estimateId }),
        listEstimateMessages({ estimateId, limit: 200 }),
        getEstimateDesign({ estimateId }),
      ]);
      if (cancelled) return;
      if ("error" in e) {
        setError(e.error.error.message);
        setLoading(false);
        return;
      }
      setEstimate(e.data);
      setCustomerPhone((e.data as AdminEstimate).customer_phone ?? null);
      if (!("error" in m)) setMessages(m.data);
      if ("data" in d) setDesign(d.data);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [estimateId]);

  if (!estimateId) {
    return (
      <div className="mx-auto max-w-content px-12 py-16">
        <FormError message="No estimate id in URL." />
      </div>
    );
  }
  if (loading) {
    return (
      <div className="mx-auto max-w-content px-12 py-16">
        <p className="text-warm-gray">Loading review…</p>
      </div>
    );
  }
  if (error || !estimate) {
    return (
      <div className="mx-auto max-w-content px-12 py-16">
        <h1 className="mb-4 font-serif text-3xl text-starz-navy">Couldn't open this estimate.</h1>
        <FormError message={error ?? "Estimate not found."} />
        <p className="mt-6 text-sm">
          <Link to="/admin/queue" className="text-starz-gold underline">
            ← Back to queue
          </Link>
        </p>
      </div>
    );
  }

  const lineItemsLocked = estimate.status !== "in_review" && estimate.status !== "submitted";
  const photoCount = photosState.photos.length;
  const anchorCount = estimate.anchor_count ?? 0;

  // Customer-shared workspace cards (no admin-only data here).
  const customerCards = (
    <>
      <ProjectDetailsCard estimate={estimate} phone={customerPhone} snapshot={null} />
      <PhotoGallery
        estimateId={estimateId}
        photos={photosState.photos}
        needsScale={anchorCount === 0}
        readOnly
        onUploaded={photosState.prepend}
        onDeleted={photosState.remove}
      />
      {/* Phase 19 — customer-described dimensions, surfaced above
       * the AI-derived rollup so the admin sees the project-specific
       * wall area (e.g., "14 ft × 10 ft = 140 sqft" for a closet
       * build-out) at a glance. Hidden when no known_dimensions
       * anchors exist on the estimate. */}
      <ProjectDimensionsPanel estimateId={estimateId} />
      <DimensionsPanel rollup={estimate.dimensions_rollup} hasAnchor={anchorCount > 0} />
    </>
  );

  // Admin-only cards: review actions, internal notes, line-item override editor.
  const adminCards = (
    <>
      <AdminReviewActions estimate={estimate} onUpdated={(updated) => setEstimate(updated)} />
      <NotesPanel estimateId={estimateId} />
      <AdminSnapshotPanel estimateId={estimateId} isReadOnly={lineItemsLocked} />
      <AdminRendersGallery
        estimateId={estimateId}
        onOpenLightbox={(src, alt) => setLightbox({ src, alt })}
      />
      <ApprovedSketchPanel estimateId={estimateId} />
    </>
  );

  return (
    <div className="mx-auto max-w-content px-6 py-12 md:px-12">
      <EstimateHeader
        estimate={estimate}
        status={estimate.status}
        createdAt={estimate.created_at}
        isAdminView
        onEdit={() => {
          // Admin doesn't edit identity from this page; the customer does.
        }}
      />
      <LifecyclePanel
        estimate={estimate}
        isAdminView
        onSubmitted={(updated) => setEstimate(updated)}
      />

      <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_360px]">
        <ChatPanel
          estimateId={estimateId}
          initialMessages={messages}
          readOnly
          initialDesign={design}
        />
        <WorkspacePanel adminTools={adminCards}>{customerCards}</WorkspacePanel>
      </div>

      <MobileWorkspaceDrawer
        summary={{ photoCount, anchorCount, snapshot: null }}
      >
        {customerCards}
        <div className="flex items-center gap-3 pt-2">
          <div className="h-px flex-1 bg-starz-gold/20" />
          <span className="text-[11px] font-semibold uppercase tracking-widest text-starz-gold">
            Admin tools
          </span>
          <div className="h-px flex-1 bg-starz-gold/20" />
        </div>
        {adminCards}
      </MobileWorkspaceDrawer>

      <Lightbox
        open={lightbox !== null}
        src={lightbox?.src}
        alt={lightbox?.alt}
        onClose={() => setLightbox(null)}
      />

      <p className="mt-6 text-xs text-warm-gray">
        <Link to="/admin/queue" className="underline">
          ← Back to queue
        </Link>
      </p>
    </div>
  );
}
